import { useEffect } from 'react';
import { trackViewItem } from '@v2/shared/lib/analytics';
import MainPanel from './MainPanel';
import FeaturesPanel from './FeaturesPanel';
import BackIssuesPanel from './BackIssuesPanel';
import TitlesPanel from './TitlesPanel';
import PricingPanel from './PricingPanel';
import FaqPanel from './FaqPanel';
import ProductReviewsPanel from '@v2/shared/components/ProductReviewsPanel';
import { Props as PageProps } from './types';

export type Props = PageProps & {
  saleEndsOn: string;
};

/**
 * The Magzter Gold landing page, previously served by headless_jshop at /magzter-gold.
 *
 * It is a product page in disguise: every price, the star rating and the review widget
 * all come from the one product MagzterGoldPagePropsBuilder resolves. The panels stack
 * in the order they did at the source, and each one is a separate file for the same
 * reason it was there — PricingPanel is the only one with any logic in it.
 *
 * No <Container> here, and Layouts.FullWide rather than Layouts.Full for the same
 * reason: the hero and the two dark bands are full-bleed and each panel containers its
 * own contents, so the one thing this page must not have is a Container around all of
 * it.
 *
 * The "view all titles" page at /categories/magzter-gold has deliberately not moved and
 * is still served by headless — every link to it here is an ordinary outbound link.
 */
export default function MagzterGold({ product, offerCodeSkus, featuredTitles, offerCode, saleEndsOn }: Props) {
  // A product page in disguise reports a product view like one. The offer code is named
  // rather than priced in: the plans below are re-priced with it client-side, and GA4's
  // convention is to report the price before a coupon and say which coupon it was.
  //
  // `featuredTitles` gets no impressions. They are Magzter's magazines, not products we
  // sell — no sku, no price of ours — so a `view_item_list` of them would put ids into
  // ecommerce reports that match nothing else in them.
  useEffect(() => {
    trackViewItem(product, { coupon: offerCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MainPanel product={product} />
      <FeaturesPanel />
      <BackIssuesPanel />
      <TitlesPanel featuredTitles={featuredTitles} offerCode={offerCode} />
      <PricingPanel
        product={product}
        offerCodeSkus={offerCodeSkus}
        offerCode={offerCode}
        saleEndsOn={saleEndsOn}
      />
      <FaqPanel />
      <ProductReviewsPanel product={product} />
    </>
  );
}

MagzterGold.layoutName = 'Layouts.FullWide';
